import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { WeatherStationService } from '../services/weather-station.service';
import { SyncService } from '../services/sync-service';

/** Which map page the user reached the field dashboard from, so Map Analysis can go back there. */
export type DashboardOrigin = 'mapping' | 'maps';

/**
 * Whether a field click opens the Southern Gateway field dashboard rather than a map's own field
 * view, and the navigation to it. Same rule as the old mapping page (mapping.component.ts, field
 * click): Southern Gateway only, and only for a tenant with a weather station or FMS data.
 */
@Injectable({ providedIn: 'root' })
export class FieldDashboardNavService {
  /** Per tenant. The answer does not change within a session, and a field click should not wait on it twice. */
  private eligible = new Map<number, Observable<boolean>>();

  constructor(
    private weatherStationService: WeatherStationService,
    private syncService: SyncService,
    private router: Router
  ) { }

  /** Same test as mapping.component.ts. */
  isSouthernGateway(): boolean {
    const env = environment as any;
    return env.theme?.title === 'Southern Gateway' ||
      env.apiBaseUrl?.includes('southern-gateway') ||
      (env.environmentName && env.environmentName.includes('southern-gateway'));
  }

  usesDashboard(tenantId: number): Observable<boolean> {
    if (!tenantId || !this.isSouthernGateway()) {
      return of(false);
    }
    let cached = this.eligible.get(tenantId);
    if (!cached) {
      cached = forkJoin({
        hasStation: this.weatherStationService.hasTenantWeatherStation(tenantId),
        hasFms: this.syncService.hasTenantFmsData(tenantId)
      }).pipe(
        map(({ hasStation, hasFms }) => hasStation || hasFms),
        // A failed check falls back to the map's own field view, and is asked again next click.
        catchError(() => {
          this.eligible.delete(tenantId);
          return of(false);
        }),
        shareReplay(1)
      );
      this.eligible.set(tenantId, cached);
    }
    return cached;
  }

  openDashboard(fieldId: number | string, farmId: number | string | null, tenantId: number, from: DashboardOrigin): void {
    this.router.navigate(['/field-dashboard-view', fieldId, 'analysis'], {
      queryParams: { farmId, tenantId, from }
    });
  }
}
